import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, CalendarPlus, Check, FileText, Loader2, Mail, MapPin, Pencil,
  Phone, Repeat, User2, Clock3, History, Package, Send, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, fdatetime, money, relative, today, addDays } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Field } from "./LeadForm";
import { cn } from "@/lib/utils";

const STATUSES = ["New Inquiry", "Contacted", "Follow-up Required", "Interested", "Quotation Created",
  "Negotiation", "Proforma Invoice Created", "Invoice Created", "Converted", "Lost", "On Hold"];
const ACTIONS = ["Call back", "Send quotation", "Schedule demo", "Site visit", "Share catalogue", "Negotiate price", "Close deal"];

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="break-words text-sm">{value || "\u2014"}</p>
    </div>
  </div>
);

export const LeadDetail = ({ leadId, open, onOpenChange, onChanged, onEdit }) => {
  const { isAdmin, can, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [fu, setFu] = useState({
    date: today(), time: "10:00", remarks: "", next_date: addDays(3),
    next_action: "Call back", status: "Completed", lead_status: "",
  });

  const load = async (silent = false) => {
    if (!leadId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      setData(data);
      setFu((f) => ({ ...f, lead_status: data.lead.status }));
    } catch (e) {
      toast.error(errMsg(e, "Could not open this lead."));
      onOpenChange(false);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [leadId, open]);

  const lead = data?.lead;
  // first assignment is open to anyone who can work on leads; reassigning stays admin only
  const canAssign = isAdmin || (can("crm", "edit") && !!lead && !lead.assigned_to);

  useEffect(() => {
    if (open && canAssign) api.get("/staff/assignable").then((r) => setStaff(r.data)).catch(() => {});
  }, [open, canAssign]);

  const timeline = useMemo(() => {
    if (!data) return [];
    const out = [];
    (data.followups || []).forEach((f) => out.push({
      at: `${f.date}T${(f.time || "00:00")}:00`, type: "followup",
      title: `Follow-up ${f.status?.toLowerCase()}`, body: f.remarks,
      who: f.created_by_name, extra: f.next_date ? `Next: ${fdate(f.next_date)} . ${f.next_action}` : "",
    }));
    (lead?.assignment_history || []).forEach((a) => out.push({
      at: a.at, type: "assign", title: a.from ? `Reassigned to ${a.to}` : `Assigned to ${a.to}`,
      body: a.reason || "", who: a.by,
    }));
    (data.quotations || []).forEach((q) => out.push({ at: q.created_at, type: "doc", title: `Quotation ${q.number} created`, body: money(q.total), who: q.created_by_name }));
    (data.proformas || []).forEach((q) => out.push({ at: q.created_at, type: "doc", title: `Proforma invoice ${q.number} created`, body: money(q.total), who: q.created_by_name }));
    (data.invoices || []).forEach((q) => out.push({ at: q.created_at, type: "doc", title: `Invoice ${q.number} created`, body: money(q.total), who: q.created_by_name }));
    (data.logs || []).filter((l) => l.action === "update").forEach((l) => out.push({
      at: l.created_at, type: "log", title: l.record,
      body: (l.changes || []).map((c) => `${c.field}: ${c.from} -> ${c.to}`).join(", "), who: l.user_name,
    }));
    out.push({ at: lead?.created_at, type: "create", title: "Lead created", body: lead?.inquiry, who: lead?.created_by_name });
    return out.filter((o) => o.at).sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [data, lead]);

  const saveFollowup = async (e) => {
    e.preventDefault();
    if (!fu.remarks.trim()) return toast.error("Please add a short remark.");
    setSaving(true);
    try {
      await api.post("/followups", { ...fu, status: "Completed", lead_id: leadId });
      toast.success("Follow-up saved" + (fu.next_date ? " and next one scheduled" : ""));
      setFu({ ...fu, remarks: "" });
      await load(true);
      onChanged?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setSaving(false); }
  };

  const doAssign = async () => {
    if (!assignTo) return;
    try {
      await api.post(`/leads/${leadId}/assign`, { assigned_to: assignTo });
      toast.success("Lead assignment updated");
      setAssignTo("");
      await load();
      onChanged?.();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/leads/${leadId}`, { status });
      toast.success(`Status set to ${status}`);
      await load();
      onChanged?.();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto scroll-slim p-0 sm:max-w-2xl">
        <SheetTitle className="sr-only">{lead?.customer_name || "Lead"}</SheetTitle>
        <SheetDescription className="sr-only">
          Lead overview, follow-ups, timeline and documents.
        </SheetDescription>
        {loading || !lead ? (
          <div className="grid h-full place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="flex min-h-full flex-col">
            {/* Header */}
            <div className="grain relative border-b border-border bg-gradient-panel px-6 pb-5 pt-6">
              <div className="flex items-start gap-4">
                <UserAvatar name={lead.customer_name} size="lg" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-display text-xl font-bold">{lead.customer_name}</h2>
                    <StatusBadge status={lead.status} />
                    {lead.is_customer && <StatusBadge status="Customer" tone="success" />}
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">{lead.company || "Individual"}</p>
                  <p className="num mt-1 text-xs text-muted-foreground">{lead.lead_no}</p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {can("crm", "edit") && (
                  <Button size="sm" variant="outline" onClick={() => onEdit?.(lead)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                )}
                {lead.phone && (
                  <Button size="sm" variant="outline" asChild><a href={`tel:${lead.phone}`}><Phone className="h-3.5 w-3.5" /> Call</a></Button>
                )}
                {lead.phone && (
                  <Button size="sm" variant="outline" asChild>
                    <a
                      href={`https://wa.me/${lead.phone.replace(/\D/g, "")}?text=${encodeURIComponent(
                        `Hello ${lead.customer_name}, this is ${user?.name} from Megatronix Vision regarding your enquiry${lead.product ? ` for ${lead.product}` : ""}. `
                      )}`}
                      target="_blank" rel="noopener noreferrer"
                    >
                      <MessageCircle className="h-3.5 w-3.5 text-success" /> WhatsApp
                    </a>
                  </Button>
                )}
                {lead.email && (
                  <Button size="sm" variant="outline" asChild><a href={`mailto:${lead.email}`}><Mail className="h-3.5 w-3.5" /> Email</a></Button>
                )}
                {can("quotations", "add") && (
                  <Button size="sm" variant="soft" onClick={() => navigate(`/app/quotations?new=1&lead=${lead.id}`)}>
                    <FileText className="h-3.5 w-3.5" /> Create quotation
                  </Button>
                )}
              </div>
            </div>

            <Tabs defaultValue="overview" className="flex-1">
              <div className="sticky top-0 z-10 border-b border-border bg-background/95 px-6 pt-3 backdrop-blur">
                <TabsList className="w-full justify-start gap-1 bg-transparent p-0">
                  {[
                    ["overview", "Overview"], ["followups", "Follow-ups"], ["timeline", "Timeline"], ["docs", "Documents"],
                  ].map(([v, l]) => (
                    <TabsTrigger key={v} value={v}
                      className="rounded-none border-b-2 border-transparent px-3 pb-2.5 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none">
                      {l}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {/* Overview */}
              <TabsContent value="overview" className="m-0 space-y-5 px-6 py-5">
                {lead.last_demo_note && (
                  <div className="rounded-xl border border-success/40 bg-success-soft px-4 py-3" data-testid="lead-demo-note">
                    <p className="text-xs font-bold uppercase tracking-wider text-success">Demo / training done</p>
                    <p className="mt-0.5 text-sm">{lead.last_demo_note}</p>
                  </div>
                )}
                <div className="grid gap-x-6 sm:grid-cols-2">
                  <InfoRow icon={Phone} label="Contact number" value={lead.phone} />
                  <InfoRow icon={Phone} label="Alternate number" value={lead.alt_phone} />
                  <InfoRow icon={Mail} label="Email" value={lead.email} />
                  <InfoRow icon={Building2} label="Company" value={lead.company} />
                  <InfoRow icon={MapPin} label="City / State" value={[lead.city, lead.state].filter(Boolean).join(", ")} />
                  <InfoRow icon={Package} label="Interested product" value={lead.product} />
                  <InfoRow icon={Send} label="Lead source" value={lead.source} />
                  <InfoRow icon={User2} label="Assigned to" value={lead.assigned_to_name || "Unassigned"} />
                  <InfoRow icon={User2} label="Created by" value={`${lead.created_by_name} . ${fdatetime(lead.created_at)}`} />
                  <InfoRow icon={Clock3} label="Last updated" value={fdatetime(lead.updated_at)} />
                </div>

                {lead.address && <InfoRow icon={MapPin} label="Address" value={lead.address} />}
                {lead.inquiry && (
                  <div className="rounded-lg border border-border bg-muted/40 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Inquiry details</p>
                    <p className="mt-1 text-sm leading-relaxed">{lead.inquiry}</p>
                  </div>
                )}

                <Separator />

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Change lead status">
                    <Select value={lead.status} onValueChange={setStatus}>
                      <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  {canAssign && (
                    <Field label={lead.assigned_to ? "Reassign to" : "Assign to salesperson"}>
                      <div className="flex gap-2">
                        <Select value={assignTo} onValueChange={setAssignTo}>
                          <SelectTrigger data-testid="lead-assign-select"><SelectValue placeholder="Select salesperson" /></SelectTrigger>
                          <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button onClick={doAssign} disabled={!assignTo}><Repeat className="h-4 w-4" /></Button>
                      </div>
                    </Field>
                  )}
                </div>
              </TabsContent>

              {/* Follow-ups */}
              <TabsContent value="followups" className="m-0 space-y-5 px-6 py-5">
                {can("crm", "edit") && (
                  <form onSubmit={saveFollowup} className="space-y-4 rounded-xl border border-border bg-card p-4">
                    <p className="font-display text-sm font-semibold">Log a follow-up</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Date"><Input type="date" value={fu.date} onChange={(e) => setFu({ ...fu, date: e.target.value })} /></Field>
                      <Field label="Time"><Input type="time" value={fu.time} onChange={(e) => setFu({ ...fu, time: e.target.value })} /></Field>
                    </div>
                    <Field label="What happened?">
                      <Textarea rows={2} data-testid="fu-remarks" value={fu.remarks}
                        onChange={(e) => setFu({ ...fu, remarks: e.target.value })}
                        placeholder="Spoke to the purchase head, sharing revised pricing..." />
                    </Field>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Next follow-up date" hint="Leave empty if no further follow-up is needed.">
                        <Input type="date" value={fu.next_date} onChange={(e) => setFu({ ...fu, next_date: e.target.value })} />
                      </Field>
                      <Field label="Next action">
                        <Select value={fu.next_action} onValueChange={(v) => setFu({ ...fu, next_action: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="Move lead to">
                        <Select value={fu.lead_status || lead.status} onValueChange={(v) => setFu({ ...fu, lead_status: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <Button type="submit" disabled={saving} className="w-full" data-testid="fu-save">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
                      Save follow-up
                    </Button>
                  </form>
                )}

                {(data.followups || []).length === 0 ? (
                  <EmptyState icon={CalendarPlus} title="No follow-ups yet" description="Log your first conversation above." />
                ) : (
                  <div className="space-y-3">
                    {data.followups.map((f) => (
                      <div key={f.id} className="rounded-lg border border-border bg-card p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={f.status} />
                            <span className="text-xs text-muted-foreground">{fdate(f.date)} . {f.time}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{f.created_by_name}</span>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed">{f.remarks}</p>
                        {f.next_date && (
                          <p className="mt-2 flex items-center gap-1.5 text-xs text-primary">
                            <CalendarPlus className="h-3.5 w-3.5" /> Next: {fdate(f.next_date)} . {f.next_action}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Timeline */}
              <TabsContent value="timeline" className="m-0 px-6 py-5">
                {timeline.length === 0 ? (
                  <EmptyState icon={History} title="Nothing recorded yet" />
                ) : (
                  <ol className="relative space-y-5 border-l border-border pl-6">
                    {timeline.map((t, i) => (
                      <li key={i} className="relative">
                        <span className={cn(
                          "absolute -left-[30px] top-1 grid h-5 w-5 place-items-center rounded-full border-2 border-background",
                          t.type === "doc" ? "bg-success" : t.type === "assign" ? "bg-info"
                            : t.type === "create" ? "bg-primary" : "bg-warning"
                        )}>
                          <Check className="h-2.5 w-2.5 text-background" />
                        </span>
                        <p className="text-sm font-semibold">{t.title}</p>
                        {t.body && <p className="mt-0.5 text-sm text-muted-foreground">{t.body}</p>}
                        {t.extra && <p className="mt-0.5 text-xs text-primary">{t.extra}</p>}
                        <p className="mt-1 text-[11px] text-muted-foreground">{t.who} . {fdatetime(t.at)} . {relative(t.at)}</p>
                      </li>
                    ))}
                  </ol>
                )}
              </TabsContent>

              {/* Documents */}
              <TabsContent value="docs" className="m-0 space-y-4 px-6 py-5">
                {[["Quotations", data.quotations, "quotations"], ["Proforma Invoices", data.proformas, "proformas"],
                  ["Invoices", data.invoices, "invoices"], ["Purchase Orders", data.purchase_orders, "purchase-orders"]]
                  .map(([label, list, path]) => (
                    <div key={label}>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
                      {(!list || list.length === 0) ? (
                        <p className="rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
                          No {label.toLowerCase()} created for this lead yet.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {list.map((d) => (
                            <button key={d.id} onClick={() => navigate(`/app/${path}?open=${d.id}`)}
                              className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:border-primary/40">
                              <span>
                                <span className="num block text-sm font-semibold">{d.number}</span>
                                <span className="block text-xs text-muted-foreground">{fdate(d.date)} . {d.created_by_name}</span>
                              </span>
                              <span className="flex items-center gap-3">
                                <span className="num text-sm font-semibold">{money(d.total)}</span>
                                <StatusBadge status={d.status} />
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
